import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  createTableColumn,
  Text,
} from '@fluentui/react-components';
import type {
  TableColumnDefinition,
  SortDirection,
  TableColumnId,
} from '@fluentui/react-components';
import { WidgetContainer } from '../WidgetContainer';
import type { BusinessGroupConsumption } from '@/types/reports';
import { useGroupConsumptionTableStyles } from './GroupConsumptionTable.styles';

export interface GroupConsumptionTableProps {
  data: BusinessGroupConsumption[];
  title: string;
}

interface GroupRow extends BusinessGroupConsumption {
  shareOfTotal: number;
}

export const GroupConsumptionTable = ({ data, title }: GroupConsumptionTableProps) => {
  const styles = useGroupConsumptionTableStyles();
  const { t } = useTranslation('reports');

  const [sortState, setSortState] = useState<{
    sortColumn: TableColumnId;
    sortDirection: SortDirection;
  }>({
    sortColumn: 'bookedHours',
    sortDirection: 'descending',
  });

  const totalHours = useMemo(() => data.reduce((sum, g) => sum + g.bookedHours, 0), [data]);

  const rows: GroupRow[] = useMemo(
    () =>
      data.map((g) => ({
        ...g,
        shareOfTotal: totalHours > 0 ? (g.bookedHours / totalHours) * 100 : 0,
      })),
    [data, totalHours]
  );

  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    const dir = sortState.sortDirection === 'ascending' ? 1 : -1;
    sorted.sort((a, b) => {
      switch (sortState.sortColumn) {
        case 'name':
          return dir * a.name.localeCompare(b.name);
        case 'bookedHours':
          return dir * (a.bookedHours - b.bookedHours);
        case 'bookings':
          return dir * (a.bookings - b.bookings);
        case 'utilization':
          return dir * (a.utilization - b.utilization);
        case 'shareOfTotal':
          return dir * (a.shareOfTotal - b.shareOfTotal);
        default:
          return 0;
      }
    });
    return sorted;
  }, [rows, sortState]);

  const handleSortChange = useCallback(
    (_: unknown, next: { sortColumn: TableColumnId | undefined; sortDirection: SortDirection }) => {
      if (next.sortColumn) {
        setSortState({ sortColumn: next.sortColumn, sortDirection: next.sortDirection });
      }
    },
    []
  );

  const columns: TableColumnDefinition<GroupRow>[] = useMemo(() => {
    const utilClass = (util: number) => {
      if (util >= 70) return styles.utilGreen;
      if (util >= 50) return styles.utilAmber;
      return styles.utilRed;
    };

    return [
      createTableColumn<GroupRow>({
        columnId: 'rank',
        renderHeaderCell: () => t('groupTable.rank'),
        renderCell: (item) => {
          const idx = sortedRows.findIndex((r) => r.id === item.id);
          return <Text className={styles.rank}>{idx + 1}</Text>;
        },
      }),
      createTableColumn<GroupRow>({
        columnId: 'name',
        compare: (a, b) => a.name.localeCompare(b.name),
        renderHeaderCell: () => t('groupTable.businessGroup'),
        renderCell: (item) => <Text>{item.name}</Text>,
      }),
      createTableColumn<GroupRow>({
        columnId: 'bookedHours',
        compare: (a, b) => a.bookedHours - b.bookedHours,
        renderHeaderCell: () => t('groupTable.bookedHours'),
        renderCell: (item) => <Text>{item.bookedHours.toLocaleString()}</Text>,
      }),
      createTableColumn<GroupRow>({
        columnId: 'shareOfTotal',
        compare: (a, b) => a.shareOfTotal - b.shareOfTotal,
        renderHeaderCell: () => t('groupTable.shareOfTotal'),
        renderCell: (item) => (
          <div className={styles.shareBar}>
            <div className={styles.shareBarTrack}>
              <div
                className={styles.shareBarFill}
                style={{ width: `${item.shareOfTotal}%`, backgroundColor: item.color }}
                aria-hidden="true"
              />
            </div>
            <span className={styles.shareText}>{item.shareOfTotal.toFixed(1)}%</span>
          </div>
        ),
      }),
      createTableColumn<GroupRow>({
        columnId: 'bookings',
        compare: (a, b) => a.bookings - b.bookings,
        renderHeaderCell: () => t('groupTable.bookings'),
        renderCell: (item) => <Text>{item.bookings.toLocaleString()}</Text>,
      }),
      createTableColumn<GroupRow>({
        columnId: 'utilization',
        compare: (a, b) => a.utilization - b.utilization,
        renderHeaderCell: () => t('groupTable.avgUtilization'),
        renderCell: (item) => (
          <Text className={utilClass(item.utilization)}>{item.utilization}%</Text>
        ),
      }),
    ];
  }, [t, styles, sortedRows]);

  const translatedTitle = t(title);

  return (
    <WidgetContainer title={translatedTitle}>
      <div className={styles.root}>
        <DataGrid
          items={sortedRows}
          columns={columns}
          sortable
          sortState={sortState}
          onSortChange={handleSortChange}
          aria-label={translatedTitle}
          getRowId={(item: GroupRow) => item.id}
        >
          <DataGridHeader>
            <DataGridRow>
              {({ renderHeaderCell }) => (
                <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
              )}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<GroupRow>>
            {({ item, rowId }) => (
              <DataGridRow<GroupRow> key={rowId}>
                {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      </div>
    </WidgetContainer>
  );
};

GroupConsumptionTable.displayName = 'GroupConsumptionTable';
